import { gettext } from "@/services/translation";

export enum CookieName {
  AuthToken = "AuthToken",
  LoginMarker = "LoginMarker",
  Poll = "Poll",
  Preferences = "Preferences",
}

export enum CookiePurposeCategory {
  Technical = "Technical",
}

export const CookiePurposeCategoryLabels: Record<CookiePurposeCategory, () => string> = {
  [CookiePurposeCategory.Technical]: () => gettext.$gettext("Technické"),
};

type CookieDefinition = {
  name: string;
  category: CookiePurposeCategory;
  expirationDays: number;
  // Wrapped in a function (rather than a plain string) so the literal text stays extractable by
  // gettext:extract while still being translated reactively, at call time, to the active language.
  getExpirationLabel: () => string;
  getPurpose: () => string;
};

/**
 * Single source of truth for every first-party cookie the app sets.
 * `src/views/legal/PrivacyPolicy.vue` renders its cookie table from this registry,
 * so it can't drift out of sync with what the app actually sets.
 *
 * Third-party analytics cookies we don't set ourselves (_ga, _gid, _utm.gif) are not
 * routed through our cookie service, so they stay manually documented in that table.
 */
export const CookieRegistry: Record<CookieName, CookieDefinition> = {
  [CookieName.AuthToken]: {
    // __Host- prefix enforces Secure + path=/ + no Domain — only valid over HTTPS.
    // On HTTP (local dev) we fall back to a plain name.
    name: import.meta.env.PROD ? "__Host-token" : "colectio_token",
    category: CookiePurposeCategory.Technical,
    expirationDays: 30,
    getExpirationLabel: () => gettext.$gettext("30 dní"),
    getPurpose: () => gettext.$gettext("Uchovává tvůj přihlašovací token, díky kterému zůstáváš přihlášený."),
  },
  [CookieName.LoginMarker]: {
    name: "dsr_swli",
    category: CookiePurposeCategory.Technical,
    expirationDays: 365,
    getExpirationLabel: () => gettext.$gettext("1 rok"),
    getPurpose: () => gettext.$gettext("Vylepšuje tvůj zážitek z přihlašování."),
  },
  [CookieName.Poll]: {
    name: "poll",
    category: CookiePurposeCategory.Technical,
    expirationDays: 30,
    getExpirationLabel: () => gettext.$gettext("30 dní"),
    getPurpose: () => gettext.$gettext("Ukládá tvé hlasování v anketách, díky čemuž můžeš v každé anketě hlasovat jen jednou."),
  },
  [CookieName.Preferences]: {
    name: "colectio_preferences",
    category: CookiePurposeCategory.Technical,
    expirationDays: 365,
    getExpirationLabel: () => gettext.$gettext("1 rok"),
    getPurpose: () => gettext.$gettext("Ukládá tvé předvolby pro toto zařízení — např. naposledy vybranou kategorii, zvolený způsob zobrazení tematických seznamů nebo zavřené nápovědy — aby se ti aplikace chovala podle tvého nastavení."),
  },
};
